import { useMemo, useState } from "react";
import { Receipt, Wallet, ChevronRight, ArrowLeft, CircleCheck, Building2, Copy, Check } from "lucide-react";

import { api } from "../lib/api";
import { useResource } from "../lib/useApi";
import { money, date, relDays, daysFromToday } from "../lib/fmt";
import { Sheet, Glass, Skeleton, Err, Empty, Pill } from "../ui";
import { PayBox } from "./PayBox";

/**
 * PaymentsSheet — the invoice list, one invoice, and its receipts.
 *
 * The summary block renders whatever the server sends. As of V8
 * portal_invoice_summary() returns the full breakdown (subtotal, discount,
 * GST) rather than only the three totals, which is why the Subtotal and
 * Discount rows can exist here at all — the old shape simply had no field to
 * print. Every row is guarded, so this file is safe against a server that has
 * not been upgraded yet: it falls back to the three totals.
 */

export function PaymentsSheet({ ctx, onClose }) {
  const { pid, studio } = ctx;
  const { data, loading, error, reload } = useResource(`/portal/payments?project_id=${pid}`);
  const [openInv, setOpenInv] = useState(null);

  const totals = useMemo(() => {
    let billed = 0, paid = 0, due = 0, next = null;
    for (const inv of data || []) {
      const s = inv.summary || {};
      billed += Number(s.grand_total || 0);
      paid += Number(s.paid || 0);
      const b = Number(s.balance || 0);
      if (b > 0.5) {
        due += b;
        if (inv.due_date && (!next || inv.due_date < next)) next = inv.due_date;
      }
    }
    return { billed, paid, due, next };
  }, [data]);

  const invoice = (data || []).find((i) => i.id === openInv);

  /* The invoice with the earliest due date and something still on it — the one
     "Pay now" on Home is about, and the one this sheet offers first. */
  const nextDue = (data || [])
    .filter((i) => Number(i?.summary?.balance || 0) > 0.5)
    .sort((a, b) => String(a.due_date || "").localeCompare(String(b.due_date || "")))[0];

  if (invoice) {
    return (
      <Sheet
        title={
          <div style={{ display: "flex", alignItems: "center", gap: 10, flex: 1 }}>
            <button className="bar-btn" onClick={() => setOpenInv(null)} aria-label="Back to invoices">
              <ArrowLeft size={18} />
            </button>
            <h2 style={{ margin: 0, fontFamily: "var(--font-head)", fontSize: "1.4rem" }}>
              Invoice #{invoice.id}
            </h2>
          </div>
        }
        onClose={onClose}
      >
        <InvoiceDetail invoice={invoice} studio={studio} onPaid={reload} />
      </Sheet>
    );
  }

  return (
    <Sheet title="Payments" onClose={onClose}>
      {error && <Err onDismiss={reload}>{error}</Err>}

      {loading ? (
        <Skeleton h={120} />
      ) : (
        <Glass strong style={{ padding: 18, marginBottom: 16, borderRadius: 24 }}>
          <p className="sec-title" style={{ padding: 0, marginTop: 0 }}>
            {totals.due > 0.5 ? "Balance due" : "All settled"}
          </p>
          <p className="hero-amt" style={{ textAlign: "left", margin: "6px 0 10px" }}>
            {money(totals.due > 0.5 ? totals.due : totals.paid)}
          </p>
          {totals.due > 0.5 ? (
            <p className="muted" style={{ margin: 0, fontSize: ".84rem" }}>
              {totals.next ? <>Earliest due {date(totals.next)} — {relDays(totals.next)}.</> : "Against your approved schedule."}
            </p>
          ) : (
            <p className="muted" style={{ margin: 0, fontSize: ".84rem" }}>
              Paid in full against {money(totals.billed)} billed. Thank you.
            </p>
          )}
          <div className="grid2" style={{ marginTop: 14 }}>
            <div className="neu-in stat"><small>Billed</small><b>{money(totals.billed)}</b></div>
            <div className="neu-in stat"><small>Received</small><b>{money(totals.paid)}</b></div>
          </div>
        </Glass>
      )}

      {nextDue && <PayBox invoice={nextDue} onPaid={reload} />}

      {!loading && !data?.length && (
        <Empty icon={Receipt} title="No invoices raised yet" note="They will appear here the moment one is issued." />
      )}

      {(data || []).map((inv) => {
        const s = inv.summary || {};
        const bal = Number(s.balance || 0);
        const overdue = bal > 0.5 && inv.due_date && daysFromToday(inv.due_date) < 0;
        return (
          <Glass as="button" className="row" key={inv.id} onClick={() => setOpenInv(inv.id)}>
            <span className="row-ic"><Receipt size={17} /></span>
            <span className="row-txt">
              <b>Invoice #{inv.id}</b>
              <small>{date(inv.bill_date)}{inv.due_date ? ` · due ${date(inv.due_date)}` : ""}</small>
            </span>
            <span className="row-end">
              <b>{money(s.grand_total)}</b>
              {bal > 0.5
                ? <Pill tone={overdue ? "due" : "warn"}>{overdue ? "Overdue" : money(bal) + " due"}</Pill>
                : <Pill tone="ok">Paid</Pill>}
            </span>
            <ChevronRight size={16} color="var(--ink-soft)" />
          </Glass>
        );
      })}

      {studio && <BankBlock studio={studio} />}
    </Sheet>
  );
}

/* ---- one invoice --------------------------------------------------------- */

function InvoiceDetail({ invoice, studio, onPaid }) {
  const s = invoice.summary || {};
  const bal = Number(s.balance || 0);

  /* Only render a row the server actually sent a number for. A hard-coded row
     showing "Subtotal —" is worse than no row: it reads as a missing figure
     rather than as a figure that does not apply. */
  const rows = [
    ["Subtotal", s.subtotal],
    [discountLabel(s), s.discount, true],
    [s.tax_pct ? `GST ${s.tax_pct}%` : "GST", s.gst],
  ].filter(([, v]) => v !== undefined && v !== null && Number(v) !== 0);

  return (
    <>
      <div style={{ display: "flex", gap: 10, marginBottom: 14 }}>
        <Pill tone={bal > 0.5 ? "due" : "ok"}>{bal > 0.5 ? "Balance " + money(bal) : "Paid in full"}</Pill>
        {invoice.due_date && <Pill>Due {date(invoice.due_date)}</Pill>}
      </div>

      <Glass style={{ padding: 16, marginBottom: 14 }}>
        {(invoice.items || []).map((it, i) => (
          <div key={i} style={{ padding: "10px 0", borderTop: i ? "1px solid rgba(201,196,182,.45)" : 0 }}>
            <div style={{ display: "flex", gap: 10 }}>
              <b style={{ flex: 1, fontWeight: 500, fontSize: ".9rem" }}>{it.title}</b>
              <span className="money" style={{ fontSize: ".9rem" }}>{money(it.total)}</span>
            </div>
            <small className="muted" style={{ fontSize: ".74rem" }}>
              {Number(it.quantity).toLocaleString("en-IN", { maximumFractionDigits: 2 })} {it.unit_type} × {money(it.rate)}
            </small>
          </div>
        ))}
      </Glass>

      <Glass tint style={{ padding: 16, marginBottom: 14 }}>
        {rows.map(([label, value, negative]) => (
          <Line key={label} label={label} value={(negative ? "− " : "") + money(Math.abs(Number(value)))} />
        ))}
        <Line label="Final value" value={money(s.grand_total)} strong />
        <Line label="Received" value={money(s.paid)} />
        <Line label={bal > 0.5 ? "Balance due" : "Balance"} value={money(bal)} strong tone={bal > 0.5 ? "due" : "ok"} />
      </Glass>

      {(invoice.payments || []).length > 0 && (
        <>
          <p className="sec-title" style={{ padding: "0 0 8px" }}>Receipts</p>
          {invoice.payments.map((p) => (
            <Glass className="row" key={p.id}>
              <span className="row-ic" style={{ background: "rgba(46,139,111,.14)", color: "var(--ok)" }}>
                <CircleCheck size={17} />
              </span>
              <span className="row-txt">
                <b>{money(p.amount)}</b>
                <small>{date(p.payment_date)} · {p.payment_method || "—"}</small>
              </span>
              {p.transaction_id && <span className="mono" style={{ fontSize: ".68rem", color: "var(--ink-soft)" }}>{p.transaction_id}</span>}
            </Glass>
          ))}
        </>
      )}

      {bal > 0.5 && <PayBox invoice={invoice} onPaid={onPaid} />}
      {bal > 0.5 && studio && <BankBlock studio={studio} amount={bal} />}
    </>
  );
}

function Line({ label, value, strong, tone }) {
  return (
    <div style={{ display: "flex", gap: 12, padding: "7px 0" }}>
      <span style={{ flex: 1, fontSize: strong ? ".92rem" : ".85rem", fontWeight: strong ? 600 : 400, color: strong ? "var(--ink)" : "var(--ink-soft)" }}>
        {label}
      </span>
      <span
        className={strong ? "money" : ""}
        style={{
          fontSize: strong ? "1rem" : ".88rem",
          color: tone === "due" ? "var(--mag-btn)" : tone === "ok" ? "var(--ok)" : "var(--ink)",
        }}
      >
        {value}
      </span>
    </div>
  );
}

/**
 * How to pay. The portal does not take card payments — there is no gateway
 * wired in yet — so this shows the studio's own bank details rather than a
 * button that would 404. When a gateway lands, this block is what it replaces.
 */
function BankBlock({ studio, amount }) {
  const [copied, setCopied] = useState("");
  if (!studio?.bank_account) return null;

  const copy = (label, value) => {
    navigator.clipboard?.writeText(value).then(() => {
      setCopied(label);
      setTimeout(() => setCopied(""), 1800);
    }).catch(() => {});
  };

  const fields = [
    ["Account name", studio.name],
    ["Account number", studio.bank_account],
    ["IFSC", studio.bank_ifsc],
    ["Bank", studio.bank_name],
  ].filter(([, v]) => v);

  return (
    <Glass style={{ padding: 16, marginTop: 16 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
        <span className="row-ic"><Building2 size={17} /></span>
        <b style={{ fontFamily: "var(--font-bold)" }}>How to pay</b>
      </div>
      {amount ? (
        <p className="muted" style={{ margin: "0 0 10px", fontSize: ".84rem" }}>
          Transfer {money(amount)} to the account below and send us the reference — we will post the receipt here.
        </p>
      ) : null}
      {fields.map(([label, value]) => (
        <button
          key={label}
          onClick={() => copy(label, String(value))}
          style={{ display: "flex", width: "100%", gap: 10, padding: "9px 0", borderTop: "1px solid rgba(201,196,182,.45)", textAlign: "left" }}
        >
          <span style={{ flex: 1, fontSize: ".78rem", color: "var(--ink-soft)" }}>{label}</span>
          <span className="mono" style={{ fontSize: ".8rem" }}>{value}</span>
          {copied === label ? <Check size={14} color="var(--ok)" /> : <Copy size={14} color="var(--ink-soft)" />}
        </button>
      ))}
    </Glass>
  );
}

function discountLabel(s) {
  if (s.discount_label) return s.discount_label;
  if (s.discount_mode === "percent" && s.discount_pct) return `Discount (${s.discount_pct}%)`;
  return "Discount";
}

export { Wallet };
